import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Trophy, ToggleLeft, ToggleRight, AlertTriangle, Info,
    RefreshCw, Save, DownloadCloud, Trash2, ListOrdered
} from 'lucide-react';
import Card from '../../components/Card';
import Select from '../../components/Select';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import {
    getBasketballCompetitions,
    getAdminStandings,
    setManualStandingsOverride,
    saveManualStandings,
    seedManualStandingsFromApi,
    clearManualStandings
} from '../../utils/api';
import toast from 'react-hot-toast';

// Numeric fields the admin can edit per team row.
const EDITABLE_FIELDS = ['position', 'gamesPlayed', 'wins', 'losses', 'points'];

// Build a row's initial editable values, preferring existing manual data and
// falling back to the synced (API) standing so admins start from real numbers.
function buildRowState(teamRow) {
    const source = teamRow.manualStanding || teamRow.apiStanding || {};
    return {
        position: source.position ?? '',
        gamesPlayed: source.gamesPlayed ?? '',
        wins: source.wins ?? '',
        losses: source.losses ?? '',
        points: source.points ?? ''
    };
}

function toNumberOrNull(value) {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

export default function StandingsManagement() {
    const queryClient = useQueryClient();
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [editedRows, setEditedRows] = useState({}); // teamId -> { field: value }
    const [showClearModal, setShowClearModal] = useState(false);

    // Fetch competitions
    const { data: competitionsData, isLoading: competitionsLoading } = useQuery({
        queryKey: ['basketball-competitions'],
        queryFn: getBasketballCompetitions
    });

    // Default to first competition
    useEffect(() => {
        if (competitionsData?.data?.length > 0 && !selectedCompetition) {
            setSelectedCompetition(competitionsData.data[0]._id);
        }
    }, [competitionsData, selectedCompetition]);

    // Fetch standings editor data
    const {
        data: standingsData,
        isLoading: standingsLoading,
        refetch: refetchStandings
    } = useQuery({
        queryKey: ['admin-standings', selectedCompetition],
        queryFn: () => getAdminStandings(selectedCompetition),
        enabled: !!selectedCompetition
    });

    const payload = standingsData?.data;
    const useManualStandings = payload?.useManualStandings || false;
    const hasManualStandings = payload?.hasManualStandings || false;
    const teamRows = useMemo(() => payload?.teams || [], [payload]);

    // Reset local edits whenever the loaded competition/data changes.
    useEffect(() => {
        const initial = {};
        teamRows.forEach((row) => {
            initial[row.teamId] = buildRowState(row);
        });
        setEditedRows(initial);
    }, [teamRows]);

    const invalidate = () => {
        queryClient.invalidateQueries(['admin-standings', selectedCompetition]);
    };

    // Toggle override mutation
    const toggleMutation = useMutation({
        mutationFn: (enabled) => setManualStandingsOverride(selectedCompetition, enabled),
        onSuccess: (result) => {
            const enabled = result.data?.useManualStandings;
            toast.success(`Manual standings ${enabled ? 'ON' : 'OFF'} — app now reads ${enabled ? 'manual' : 'official'} data`);
            invalidate();
        },
        onError: (error) => toast.error(error.message || 'Failed to update override')
    });

    // Save mutation
    const saveMutation = useMutation({
        mutationFn: (rows) => saveManualStandings(selectedCompetition, rows),
        onSuccess: (result) => {
            toast.success(result.message || 'Standings saved');
            invalidate();
        },
        onError: (error) => toast.error(error.message || 'Failed to save standings')
    });

    // Seed mutation
    const seedMutation = useMutation({
        mutationFn: () => seedManualStandingsFromApi(selectedCompetition),
        onSuccess: (result) => {
            toast.success(result.message || 'Copied from official standings');
            invalidate();
        },
        onError: (error) => toast.error(error.message || 'Failed to copy from official standings')
    });

    // Clear mutation
    const clearMutation = useMutation({
        mutationFn: () => clearManualStandings(selectedCompetition),
        onSuccess: (result) => {
            toast.success(result.message || 'Manual standings cleared');
            setShowClearModal(false);
            invalidate();
        },
        onError: (error) => toast.error(error.message || 'Failed to clear manual standings')
    });

    const handleFieldChange = (teamId, field, value) => {
        setEditedRows((prev) => ({
            ...prev,
            [teamId]: { ...prev[teamId], [field]: value }
        }));
    };

    const handleSave = () => {
        const rows = teamRows.map((teamRow) => {
            const edited = editedRows[teamRow.teamId] || {};
            return {
                teamId: teamRow.teamId,
                position: toNumberOrNull(edited.position) ?? 0,
                gamesPlayed: toNumberOrNull(edited.gamesPlayed),
                wins: toNumberOrNull(edited.wins) ?? 0,
                losses: toNumberOrNull(edited.losses) ?? 0,
                points: toNumberOrNull(edited.points) ?? 0
            };
        });
        saveMutation.mutate(rows);
    };

    const competitionName = payload?.competition
        ? `${payload.competition.name} (${payload.competition.season})`
        : '';

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Standings Management</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Manually edit standings and override the official API data shown in the app
                    </p>
                </div>
                <button
                    onClick={() => refetchStandings()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <RefreshCw size={18} />
                    Refresh
                </button>
            </div>

            {/* Competition Selector */}
            <Card>
                <div className="flex items-center gap-4">
                    <Trophy size={20} className="text-primary" />
                    <Select
                        value={selectedCompetition || ''}
                        onChange={(e) => setSelectedCompetition(e.target.value)}
                        className="flex-1"
                        disabled={competitionsLoading}
                    >
                        <option value="">Select Competition</option>
                        {competitionsData?.data?.map((comp) => (
                            <option key={comp._id} value={comp._id}>
                                {comp.name} ({comp.season})
                            </option>
                        ))}
                    </Select>
                </div>
            </Card>

            {/* Override Toggle */}
            <Card>
                <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                        <ListOrdered size={24} className="text-primary" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-semibold mb-2">Standings Data Source</h2>
                        <p className="text-muted-light dark:text-muted-dark mb-4">
                            Control whether the app reads standings from the official API (Genius Sports)
                            or from the manual standings you edit below.
                        </p>

                        <div className={`p-4 rounded-lg border mb-4 ${useManualStandings
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                            }`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-lg">
                                        {useManualStandings ? 'Manual Standings (Override ON)' : 'Official API (Override OFF)'}
                                    </div>
                                    <p className="text-sm text-muted-light dark:text-muted-dark mt-1">
                                        {useManualStandings
                                            ? 'The app is showing the manual standings you edited below.'
                                            : 'The app is showing official standings synced from Genius Sports.'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => toggleMutation.mutate(!useManualStandings)}
                                    disabled={!selectedCompetition || standingsLoading || toggleMutation.isPending}
                                    className="flex-shrink-0 p-2 hover:bg-white/50 dark:hover:bg-black/20 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {useManualStandings ? (
                                        <ToggleRight size={48} className="text-green-600" />
                                    ) : (
                                        <ToggleLeft size={48} className="text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {useManualStandings && !hasManualStandings && (
                            <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                                <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-yellow-800 dark:text-yellow-200">
                                    Override is ON but no manual standings exist yet, so the app still
                                    falls back to official data. Copy from official and save below.
                                </div>
                            </div>
                        )}

                        <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                                    <li>• <strong>OFF:</strong> standings come from the official API (default).</li>
                                    <li>• <strong>ON:</strong> the app shows the manual standings below.</li>
                                    <li>• Editing the manual standings does not change anything in the app until the toggle is ON.</li>
                                    <li>• The official sync keeps running in the background and never overwrites your manual edits.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Manual Standings Editor */}
            <Card>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                        <ListOrdered size={20} className="text-primary" />
                        <h2 className="text-xl font-semibold">
                            Manual Standings {competitionName && `— ${competitionName}`}
                        </h2>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={() => seedMutation.mutate()}
                            disabled={!selectedCompetition || seedMutation.isPending}
                            loading={seedMutation.isPending}
                        >
                            <DownloadCloud size={16} />
                            Copy from official
                        </Button>
                        {hasManualStandings && (
                            <Button
                                variant="danger"
                                size="small"
                                onClick={() => setShowClearModal(true)}
                                disabled={!selectedCompetition || clearMutation.isPending}
                            >
                                <Trash2 size={16} />
                                Clear
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="small"
                            onClick={handleSave}
                            disabled={!selectedCompetition || saveMutation.isPending || teamRows.length === 0}
                            loading={saveMutation.isPending}
                        >
                            <Save size={16} />
                            Save changes
                        </Button>
                    </div>
                </div>

                {standingsLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : teamRows.length === 0 ? (
                    <p className="text-muted-light dark:text-muted-dark text-center py-8">
                        No teams found for this competition.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-muted-light dark:text-muted-dark border-b border-border-light dark:border-border-dark">
                                    <th className="py-2 pr-3 font-medium w-20">Pos</th>
                                    <th className="py-2 pr-3 font-medium">Team</th>
                                    <th className="py-2 pr-3 font-medium w-24">GP</th>
                                    <th className="py-2 pr-3 font-medium w-24">W</th>
                                    <th className="py-2 pr-3 font-medium w-24">L</th>
                                    <th className="py-2 pr-3 font-medium w-24">PTS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamRows.map((row) => {
                                    const values = editedRows[row.teamId] || {};
                                    return (
                                        <tr
                                            key={row.teamId}
                                            className="border-b border-border-light dark:border-border-dark"
                                        >
                                            <td className="py-2 pr-3">
                                                <input
                                                    type="number"
                                                    className="input w-16"
                                                    value={values.position ?? ''}
                                                    onChange={(e) => handleFieldChange(row.teamId, 'position', e.target.value)}
                                                />
                                            </td>
                                            <td className="py-2 pr-3">
                                                <div className="flex items-center gap-2">
                                                    {row.team?.logoUrl && (
                                                        <img
                                                            src={row.team.logoUrl}
                                                            alt={row.team.name}
                                                            className="w-7 h-7 rounded-full object-contain bg-white"
                                                        />
                                                    )}
                                                    <span className="font-medium">
                                                        {row.team?.nickname || row.team?.name}
                                                    </span>
                                                </div>
                                            </td>
                                            {['gamesPlayed', 'wins', 'losses', 'points'].map((field) => (
                                                <td key={field} className="py-2 pr-3">
                                                    <input
                                                        type="number"
                                                        className="input w-20"
                                                        value={values[field] ?? ''}
                                                        onChange={(e) => handleFieldChange(row.teamId, field, e.target.value)}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Clear confirmation */}
            <Modal
                isOpen={showClearModal}
                onClose={() => setShowClearModal(false)}
                title="Clear Manual Standings"
                size="small"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowClearModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => clearMutation.mutate()}
                            loading={clearMutation.isPending}
                        >
                            Confirm Clear
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                        <p className="text-sm text-red-800 dark:text-red-200">
                            This deletes all manually edited standings for this competition. If the
                            override is ON, the app will fall back to the official API standings.
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
